import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useEffect, useState, useMemo, useRef } from 'react';
import { fireSuccessConfetti } from '@/lib/confetti';

interface MilestoneTrackerProps {
  elapsedTime: number; // in seconds
  isPaused?: boolean;
  mode?: 'focus' | 'admin';
  sessionDuration?: number; // in minutes, for admin mode
  goalMinutes?: number; // for focus mode: user-selected goal (1–6 hrs)
}

// Calculate milestone configuration based on mode, session duration, and goal
function getMilestoneConfig(mode: 'focus' | 'admin', sessionDuration: number, goalMinutes?: number) {
  if (mode === 'focus') {
    // Maker mode: 6 milestones based on user goal (1–6 hrs)
    const totalSeconds = (goalMinutes || 60) * 60;
    return { count: 6, intervalSeconds: totalSeconds / 6 };
  }
  
  // Admin/Manager mode: dynamic based on session length
  const durationSeconds = sessionDuration * 60;
  
  if (sessionDuration <= 15) {
    // 15 min or less: 3 milestones
    return { count: 3, intervalSeconds: durationSeconds / 3 };
  } else if (sessionDuration <= 30) {
    // 16-30 min: 5 milestones
    return { count: 5, intervalSeconds: durationSeconds / 5 };
  } else if (sessionDuration <= 60) {
    // 31-60 min: 4 milestones (every 15 min)
    return { count: 4, intervalSeconds: durationSeconds / 4 };
  } else {
    // Over 60 min: 5 milestones evenly divided
    return { count: 5, intervalSeconds: durationSeconds / 5 };
  }
}

export function MilestoneTracker({ 
  elapsedTime, 
  isPaused = false,
  mode = 'focus',
  sessionDuration = 25,
  goalMinutes,
}: MilestoneTrackerProps) {
  const [previousAchieved, setPreviousAchieved] = useState(0);
  const [newlyAchieved, setNewlyAchieved] = useState<number | null>(null);
  
  // Calculate milestone config based on mode
  const { count: totalMilestones, intervalSeconds } = useMemo(
    () => getMilestoneConfig(mode, sessionDuration, goalMinutes),
    [mode, sessionDuration, goalMinutes]
  );
  
  // Calculate milestones
  const achievedMilestones = Math.min(Math.floor(elapsedTime / intervalSeconds), totalMilestones);
  const timeToNext = intervalSeconds - (elapsedTime % intervalSeconds);

  // In focus mode, when past goal: show overtime so the timer continues instead of stopping at 00:00
  const goalTotalSeconds = mode === 'focus' && goalMinutes != null ? goalMinutes * 60 : totalMilestones * intervalSeconds;
  const isOvertime = mode === 'focus' && elapsedTime >= goalTotalSeconds;
  const overtimeSeconds = isOvertime ? elapsedTime - goalTotalSeconds : 0;

  const hasFiredConfettiFor = useRef(0);

  // Track newly achieved milestones for animation + confetti with message
  useEffect(() => {
    if (achievedMilestones > previousAchieved && previousAchieved < totalMilestones) {
      const justAchievedIndex = achievedMilestones - 1;
      setNewlyAchieved(justAchievedIndex);
      setPreviousAchieved(achievedMilestones);

      // Fire confetti once per milestone (avoid duplicate from strict mode / double render)
      if (achievedMilestones > hasFiredConfettiFor.current) {
        hasFiredConfettiFor.current = achievedMilestones;
        const justHitSeconds = achievedMilestones * intervalSeconds;
        const totalSeconds = totalMilestones * intervalSeconds;
        const isLast = achievedMilestones >= totalMilestones;
        const nextSeconds = isLast ? totalSeconds : (achievedMilestones + 1) * intervalSeconds;
        const fmt = (s: number) =>
          s >= 3600 ? `${Math.round(s / 3600)} hr` : `${Math.round(s / 60)} min`;
        const message = isLast
          ? `Milestone reached! ${fmt(justHitSeconds)} complete.`
          : `Hit ${fmt(justHitSeconds)}! Next: ${fmt(nextSeconds)} out of ${fmt(totalSeconds)} total`;
        fireSuccessConfetti({ message });
      }

      // Clear the "new" state after animation (match slower 1s animation)
      const timer = setTimeout(() => setNewlyAchieved(null), 1000);
      return () => clearTimeout(timer);
    }
  }, [achievedMilestones, previousAchieved, totalMilestones, intervalSeconds]);

  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Mode-specific styling
  const isFocus = mode === 'focus';
  const gradientColors = isFocus 
    ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' 
    : 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)';
  const glowColor = isFocus 
    ? 'rgba(59, 130, 246, 0.5)' 
    : 'rgba(245, 158, 11, 0.5)';
  const pulseColor = isFocus 
    ? 'rgba(59, 130, 246, 0.4)' 
    : 'rgba(245, 158, 11, 0.4)';
  const borderColor = isFocus 
    ? 'border-primary/50' 
    : 'border-amber-500/50';

  return (
    <div className="flex flex-col items-center gap-4">
      {/* Countdown to next milestone, or overtime when past goal */}
      <motion.div
        className="text-xl font-light tabular-nums tracking-wider text-foreground/60"
        animate={{ opacity: isPaused ? 0.4 : 0.6 }}
      >
        {achievedMilestones < totalMilestones
          ? formatTime(timeToNext)
          : isOvertime
            ? `+${formatTime(overtimeSeconds)}`
            : '00:00'}
      </motion.div>

      {/* Milestone circles - flex-shrink-0 + aspect-square so they stay circular on iOS */}
      <div className="flex items-center gap-3">
        {Array.from({ length: totalMilestones }).map((_, index) => {
          const isAchieved = index < achievedMilestones;
          const isNext = index === achievedMilestones && achievedMilestones < totalMilestones;
          const isNew = index === newlyAchieved;

          return (
            <motion.div
              key={index}
              className={cn(
                "flex-shrink-0 w-3.5 h-3.5 aspect-square rounded-full border-2 transition-all duration-500",
                isAchieved 
                  ? "border-transparent" 
                  : isNext 
                    ? cn("bg-transparent", borderColor)
                    : "border-foreground/15 bg-foreground/5"
              )}
              style={{
                background: isAchieved ? gradientColors : undefined,
                boxShadow: isAchieved ? `0 0 12px ${glowColor}` : undefined,
                borderRadius: '50%',
              }}
              animate={isNext && !isPaused ? {
                scale: [1, 1.1, 1],
                boxShadow: [
                  `0 0 0 0 ${pulseColor}`,
                  `0 0 0 6px rgba(0, 0, 0, 0)`,
                  `0 0 0 0 ${pulseColor}`,
                ],
              } : {}}
              transition={isNext ? {
                duration: 3,
                repeat: Infinity,
                ease: 'easeInOut',
              } : {}}
              initial={isNew ? { scale: 0.5, opacity: 0 } : false}
              {...(isNew && {
                animate: { scale: [0.5, 1.3, 1], opacity: 1 },
                transition: { duration: 1, ease: [0.34, 1.56, 0.64, 1] },
              })}
            />
          );
        })}
      </div>

      {/* Label */}
      <p className="text-xs font-medium uppercase tracking-widest text-muted-foreground/50">
        {isFocus
          ? isOvertime
            ? 'Overtime'
            : 'Next milestone'
          : 'Session progress'}
      </p>
    </div>
  );
}
